import { Export } from '@tiptap-pro/extension-export';
import Bold from '@tiptap/extension-bold';
import BulletList from '@tiptap/extension-bullet-list';
import Document from '@tiptap/extension-document';
import Heading from '@tiptap/extension-heading';
import Highlight from '@tiptap/extension-highlight';
import History from '@tiptap/extension-history';
import Italic from '@tiptap/extension-italic';
import Link from '@tiptap/extension-link';
import ListItem from '@tiptap/extension-list-item';
import OrderedList from '@tiptap/extension-ordered-list';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import { useEditor } from '@tiptap/react';

import { useTranslations } from 'next-intl';
import { FC, useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';

import { IntegrationEnum } from 'gql/graphql';
import { toaster } from 'helpers/toaster';
import { usePathname, useSearchParams } from 'next/navigation';
import { useContentGenerationData } from 'providers/ContentGenerationProviders/useContentGenerationData';
import { notifyException } from 'services/appsignal';
import { ESpansException } from 'types/ESpans';
import { KeysEnum } from 'types/KeysEnum';
import { useDebouncedCallback } from 'use-debounce';
import { docxToFile } from 'utils/docxToFile';
import { extractParam } from 'utils/paramsUtil';
import { setFileToUpload, updateInputField, useUploadingContentFlow } from '../_actions';

interface IContentCreatorIntegration {
  setFileToUpload: (file: File) => void;
  useUploadingContentFlow: () => void;
  updateInputField: (data: { field: string; value: string }) => void;
  allowedTypes: string[];
  supportedFormats: string[];
}

const ContentCreatorIntegration: FC<IContentCreatorIntegration> = ({
  setFileToUpload,
  useUploadingContentFlow: uploadingContentFlow,
  updateInputField
}) => {
  const t = useTranslations();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [loading, setLoading] = useState(false);

  const id = extractParam(searchParams, KeysEnum.Id, null) as string;
  const addon = pathname.split('/translation/new/')?.at(1);

  const editor = useEditor({
    extensions: [
      Document,
      Paragraph,
      Text,
      History,
      Heading,
      TextAlign.configure({
        types: ['heading', 'paragraph']
      }),
      Bold,
      Highlight,
      Italic,
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: 'https'
      }),
      BulletList,
      ListItem,
      OrderedList,
      Export.configure({
        appId: process.env.NEXT_PUBLIC_TIPTAP_APP_ID,
        token: process.env.NEXT_PUBLIC_TIPTAP_JWT_TOKEN
      })
    ],
    content: ``,
    editorProps: {}
  });

  const { data } = useContentGenerationData(
    { id: id },
    {
      enabled: !!id && addon === IntegrationEnum.ContentCreator
    }
  );

  const fetchFiles = useCallback(async () => {
    if (addon === IntegrationEnum.ContentCreator && !!id && !loading && !!data) {
      try {
        if (!editor) {
          return setLoading(false);
        }

        setLoading(true);
        setFileToUpload(
          await docxToFile(
            editor,
            data.nodes?.at(0)?.generations?.at(0)?.content ?? '',
            'Tolq Ai - Content Creator'
          )
        );
        updateInputField({ field: 'integration', value: IntegrationEnum.ContentCreator });
        uploadingContentFlow();
      } catch (error) {
        notifyException(error, ESpansException.GetIntegration);
        toaster.error(t('messages.error.something_happen'));
      }
    }
  }, [
    addon,
    id,
    loading,
    data,
    editor,
    setFileToUpload,
    updateInputField,
    uploadingContentFlow,
    t
  ]);

  const debouncedMutation = useDebouncedCallback(fetchFiles, 1);

  useEffect(() => {
    debouncedMutation();
  }, [id, data, addon, loading, editor, fetchFiles, debouncedMutation]);

  return null;
};

export default connect(
  ({ staticData: { supportedFormats, allowedTypes } }) => ({ supportedFormats, allowedTypes }),
  {
    setFileToUpload,
    useUploadingContentFlow,
    updateInputField
  }
)(ContentCreatorIntegration);
